import { createContext, useContext, useState, useCallback } from 'react'

const HeroPreviewContext = createContext(null)

const BASE_HERO_PREVIEW_MODES = [
  { id: null,                    label: '자동',                       desc: '자동 로직 사용' },
  { id: 'shuttle:commute',       label: '등교 모드',                  desc: 'type=shuttle · context=commute' },
  { id: 'class-next',            label: '수업 전 모드',               desc: 'type=class-next/class-upcoming' },
  { id: 'class-ongoing',         label: '수업 중/다음 수업 모드',      desc: 'type=class-ongoing' },
  { id: 'meal',                  label: '점심/학식 모드',             desc: 'type=meal' },
  { id: 'class-gap',             label: '공강 모드',                  desc: 'type=class-next · reason=gap' },
  { id: 'shuttle:go-home',       label: '하교 모드',                  desc: 'type=shuttle · context=go-home' },
  { id: 'default:evening-done',  label: '밤 모드',                    desc: 'type=default · context=evening-done' },
  { id: 'class-next:early-night', label: '밤 · 첫 수업 미리보기 모드', desc: 'type=class-next · context=early · nightSkyHero' },
  { id: 'weekend-empty',         label: '주말 모드',                  desc: 'type=weekend-empty' },
  { id: 'default:no-service',    label: '운행 없음 모드',             desc: 'type=default · shuttle unavailable' },
]

function toMaxPreviewId(id) {
  return id == null ? 'max:auto' : `max:${id}`
}

export const HERO_PREVIEW_GROUPS = [
  { id: 'normal', label: '일반 모드' },
  { id: 'max', label: '맥스 모드' },
]

export const HERO_PREVIEW_MODES = [
  ...BASE_HERO_PREVIEW_MODES.map(mode => ({
    ...mode,
    group: 'normal',
    heroMode: mode.id,
    isMaxPreview: false,
  })),
  ...BASE_HERO_PREVIEW_MODES.map(mode => ({
    ...mode,
    id: toMaxPreviewId(mode.id),
    label: `맥스 ${mode.label}`,
    displayLabel: `맥스 · ${mode.label}`,
    desc: `Max Mode · ${mode.desc}`,
    group: 'max',
    heroMode: mode.id,
    isMaxPreview: true,
  })),
]

export function getHeroPreviewOption(mode) {
  return HERO_PREVIEW_MODES.find(option => option.id === mode) ?? null
}

export function getHeroPreviewHeroMode(mode) {
  return getHeroPreviewOption(mode)?.heroMode ?? mode ?? null
}

export function getHeroPreviewDisplayLabel(mode) {
  const option = getHeroPreviewOption(mode)
  return option?.displayLabel ?? option?.label ?? '자동'
}

export function isHeroPreviewMaxMode(mode) {
  return !!getHeroPreviewOption(mode)?.isMaxPreview
}

export function HeroPreviewProvider({ children }) {
  // sessionStorage 미사용: 앱 새로고침 시 preview 자동 해제 (홈 오염 방지)
  const [heroPreviewMode, setHeroPreviewModeState] = useState(null)
  const [shuttleHeroPreviewMode, setShuttleHeroPreviewModeState] = useState(null)
  // 관리자 본인 화면 전용 히어로 노출 ON/OFF — 로컬 state만 사용, 기본 OFF, 새로고침 시 초기화.
  const [heroPreviewOn, setHeroPreviewOnState] = useState(false)

  const setHeroPreviewMode = useCallback((mode) => {
    setHeroPreviewModeState(mode || null)
  }, [])

  const clearHeroPreviewMode = useCallback(() => {
    setHeroPreviewModeState(null)
  }, [])

  const setShuttleHeroPreviewMode = useCallback((mode) => {
    setShuttleHeroPreviewModeState(mode ?? null)
  }, [])

  const clearShuttleHeroPreviewMode = useCallback(() => {
    setShuttleHeroPreviewModeState(null)
  }, [])

  const toggleHeroPreviewOn = useCallback(() => {
    setHeroPreviewOnState(prev => !prev)
  }, [])

  return (
    <HeroPreviewContext.Provider value={{
      heroPreviewMode,
      setHeroPreviewMode,
      clearHeroPreviewMode,
      isHeroPreviewActive: heroPreviewMode !== null,
      shuttleHeroPreviewMode,
      setShuttleHeroPreviewMode,
      clearShuttleHeroPreviewMode,
      isShuttleHeroPreviewActive: shuttleHeroPreviewMode !== null,
      heroPreviewOn,
      toggleHeroPreviewOn,
    }}>
      {children}
    </HeroPreviewContext.Provider>
  )
}

export function useHeroPreview() {
  const ctx = useContext(HeroPreviewContext)
  if (!ctx) return {
    heroPreviewMode: null,
    setHeroPreviewMode: () => {},
    clearHeroPreviewMode: () => {},
    isHeroPreviewActive: false,
    shuttleHeroPreviewMode: null,
    setShuttleHeroPreviewMode: () => {},
    clearShuttleHeroPreviewMode: () => {},
    isShuttleHeroPreviewActive: false,
    heroPreviewOn: false,
    toggleHeroPreviewOn: () => {},
  }
  return ctx
}
